/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import { getBraindateConversation } from '@e180/domain/lib/braindate/util';

import styles from './VirtualBraindateReminderTakeOver.style';

import messages from 'plugin/virtual-braindate/l10n/virtualBraindateL10n';
import BraindateCard from 'src/shared/app/braindate/component/card/BraindateCard';
import TakeOver from 'src/shared/app/notification/component/TakeOver';
import { getNotificationData } from 'src/shared/app/notification/util/notificationUtil';
import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';
import { isConversationAboutGroupBraindate } from 'src/shared/domain/conversation/util/conversationUtil';
import PrimaryButton from 'src/shared/ui/component/button/PrimaryButton';
import CameraIcon from 'src/shared/ui/component/icon/CameraIcon';
import Subheading from 'src/shared/ui/component/text/Subheading';

import type { Braindate } from '@e180/domain/lib/braindate/type';
import type { BraindateEvent } from '@e180/domain/lib/event/type';
import type { Notification as NotificationType } from 'src/shared/app/notification/type/notificationTypes';

type Props = {
  notification: NotificationType,
  event: BraindateEvent,
  handleClick: () => void,
  handleDismiss: () => void,
};

const useStyles = createUseStyles(styles);

const VirtualBraindateReminderTakeOverDumb = ({
  notification,
  handleDismiss,
  handleClick,
}: Props): React.Node => {
  // $FlowIssue
  const braindate: Braindate = getNotificationData(notification);

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();
  const { braindateSingular } = useEventBraindateName();
  const conversation = getBraindateConversation(braindate);
  const isGroupBraindate = isConversationAboutGroupBraindate(conversation);

  /*
   |----------------------------------------------------------------------------
   | State
   |----------------------------------------------------------------------------
   */

  const [isHidden, setHidden] = React.useState(false);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onClick = () => setHidden(true);

  const onTransitionEnd = ({ target }: TransitionEvent) => {
    const { current: rootElt } = rootRef;

    if (target === rootElt && isHidden) {
      handleClick();
    }
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  const rootClasses = classNames(classes.root, {
    [`${classes['root-isHidden']}`]: isHidden,
  });

  /*
   |----------------------------------------------------------------------------
   | Refs
   |----------------------------------------------------------------------------
   */

  const rootRef = React.createRef();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <TakeOver handleDismiss={handleDismiss}>
      <div
        className={rootClasses}
        onTransitionEnd={onTransitionEnd}
        ref={rootRef}
      >
        <div className={classes.top}>
          <Subheading layoutClass={classes.heading} withWarning>
            {intl.formatMessage(messages.takeOverTitle)}
          </Subheading>
          <BraindateCard braindate={braindate} isInactive />
        </div>
        <div>
          <div className={classes.meeting}>
            <p className={classes.instructions}>
              {intl.formatMessage(messages.takeOverCTA, {
                isGroupBraindate,
                braindateSingular,
              })}
            </p>
            <PrimaryButton
              layoutClass={classes.join}
              handleClick={onClick}
              withSuccess
            >
              <CameraIcon className={classes.joinIcon} />
              {intl.formatMessage(messages.takeOverLabel, {
                isGroupBraindate,
                braindateSingular,
              })}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </TakeOver>
  );
};

export default compose(React.memo)(VirtualBraindateReminderTakeOverDumb);
